import React from 'react';
import PropTypes from 'prop-types';
import googleUtils from '../../../lib/gmap_utils';
import envAPI from '../../../api/api-env';
import listingAPI from '../../../api/api-listings-search';
import { LocationModal } from 'shared-components/search-module';

const withLocationDropdownHoc = (Component) => {
  class HOC extends React.Component {
    constructor (props) {
      super(props);

      this.onChange = this.onChange.bind(this);
      this.metroClick = this.metroClick.bind(this);
      this.poiClick = this.poiClick.bind(this);
      this.autoComplete = this.autoComplete.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusLocation = this.onFocusLocation.bind(this);
      this.onBlurLocation = this.onBlurLocation.bind(this);
      this.getBrowserTimezoneOffset = this.getBrowserTimezoneOffset.bind(this);
      this.metroClickCb = this.metroClickCb.bind(this);
      this.showEmailDropdown = this.showEmailDropdown.bind(this);
      this.toggleModal = this.toggleModal.bind(this);
      this.debounceAutoComplete = _.debounce(this.autoComplete, 250);
      this.onInputOnclick = this.onInputOnclick.bind(this);
      this.poiOnLoad = this.poiOnLoad.bind(this);

      this.initialState = {
        metros: [],
        locationPlaceholder: 'Where?',
        predictions: [],
        whichDropdown: '',
        location: '',
        dataLocation: {},
        locationQuery: {},
        showModal: false,
        placeholderColor: 'placeholder-color-change'
      };

      this.state = this.initialState;
    }

    componentDidMount () {
      const date = Date.now();
      const location = _.get(this.props, 'location', '');
      envAPI.getMetros(date, (metros) => {
        const sortedMetros = _.sortBy(metros, (m) => {
          const difference = m.standard_tz_offset + this.getBrowserTimezoneOffset();
          return (0 > difference) ? (difference * -1) : difference;
        });
        this.setState({ metros: sortedMetros, location }, () => {
          return this.autoComplete(location, this.poiOnLoad);
        });
      });
      this.props.locationRef(this.typeAheadInput);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (this.props.location !== prevProps.location) {
        this.setState({
          location: this.props.location
        }, () => {
          return this.autoComplete(this.props.location, this.poiOnLoad);
        });
        this.props.locationRef(this.typeAheadInput);
      }
    }

    onChange (e) {
      const location = e.currentTarget.value;
      this.setState({ location }, () => {
        if (location) {
          return this.debounceAutoComplete(location);
        }
        this.debounceAutoComplete.cancel();
        return this.setState({
          predictions: '',
          whichDropdown: '',
          dataLocation: {},
          locationQuery: {}
        });
      });
    }

    onBlurLocation () {
      this.setState({
        locationPlaceholder: 'Where?',
        placeholderColor: this.initialState.placeholderColor
      });
    }

    onFocusLocation () {
      const { location, dataLocation } = this.state;
      this.setState({
        locationPlaceholder: 'Enter a city, zip, or address',
        placeholderColor: ''
      });
      if (!dataLocation.hasOwnProperty('nickname')) {
        return this.autoComplete(location);
      }
      return this.setState({
        whichDropdown: ''
      });
    }

    onInputOnclick (e) {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
        e.target.select();
      }
    }

    onKeyDown (e) {
      const { predictions } = this.state;
      const firstOnList = document.getElementById('location-list-0') || document.getElementById('metro-list-0');
      if (40 === e.keyCode && firstOnList) {
        // If user presses the down arro\w,
        // we select the first item from the dropdown
        e.preventDefault();
        firstOnList.focus();
      } else if (13 === e.keyCode && predictions.length && firstOnList) {
        // If user hits enter, we select the first item from the dropdown,
        // and blur out of the input field
        firstOnList.click();
        e.target.blur();
      }
    }

    getBrowserTimezoneOffset () {
      const currentDate = new Date();
      const gmtOffset = currentDate.getTimezoneOffset() / 60;
      return gmtOffset;
    }

    autoComplete (input, callback) {
      if (!input) return null;
      const completer = googleUtils.autoCompleteService();
      const currentBounds = this.props.currentBounds;
      let bounds;

      if (currentBounds) {
        bounds = googleUtils.getBoundsFromSearchArr(currentBounds);
      }

      const options = {
        input,
        bounds,
        types: ['geocode']
      };

      return completer.getPlacePredictions(options, (predictions, status) => {
        if ('ZERO_RESULTS' === status) {
          this.setState({ whichDropdown: 'error', locationQuery: {} });
        }

        if ('OK' === status) {
          this.setState({
            predictions,
            whichDropdown: 'location'
          }, () => {
            if (callback) {
              callback();
            }
          });
        }
      });
    }

    poiOnLoad () {
      // This function runs when a location props is passed down
      const selectedPOI = this.state.predictions[0];

      const newState = {
        dataLocation: { placeId: selectedPOI.place_id }
      };

      if (this.props.searchOnMount) {
        newState.location = selectedPOI.description;
      }

      this.setState(newState, this.poiClickCb);
    }

    poiClick (e, listToggle) {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      const poiValue = _.get(e, 'currentTarget.dataset.value', '');
      const selectedPOI = _.filter(this.state.predictions, { place_id: poiValue })[0] || {};
      this.setState({ location: selectedPOI.description, dataLocation: { placeId: selectedPOI.place_id } }, this.poiClickCb);
      if (_.isFunction(listToggle)) listToggle();
    }

    metroClick (e, closeDropdownOrModal) {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      const nickname = _.get(e, 'currentTarget.dataset.value', '');
      const selectedMetro = _.filter(this.state.metros, { nickname })[0] || {};
      this.setState({
        location: selectedMetro.name,
        dataLocation: { nickname: selectedMetro.nickname, map_center: selectedMetro.map_center }
      }, this.metroClickCb);
      if (_.isFunction(closeDropdownOrModal)) closeDropdownOrModal();
    }

    metroClickCb () {
      const { location, dataLocation } = this.state;
      const createSearchLoc = location.toLowerCase().replace(/\,\s/g, '--').replace(/ /g, '-');
      const locCode = { loc_code: createSearchLoc, map_center: dataLocation.map_center };
      this.setState({
        locationQuery: locCode
      });
      if (_.isFunction(this.props.filterResults)) {
        this.props.filterResults(locCode);
      }
    }

    poiClickCb () {
      const { location, dataLocation } = this.state;
      this.props.toggleSearchDisable(true, () => {
        googleUtils.doAutoComplete(dataLocation.placeId, location, (respObj) => {
          const { error, queryObj = {}, results = [] } = respObj;
          if (error) {
            this.showEmailDropdown(results, queryObj);
          }

          return listingAPI.get(queryObj, null, (listings, response) => {
            // sometimes "response" returned by search API is not an object
            const bbox = _.get(response, 'suggested_viewport_bbox');
            const newQueryObject = bbox ?
              _.assign({}, queryObj, { viewport_bbox: `[${[bbox]}]` })
              : queryObj;
            this.setState({
              locationQuery: newQueryObject
            }, () => this.props.toggleSearchDisable(false));
          });
        });
      });
    }

    showEmailDropdown (results, queryObj) {
      this.setState({ whichDropdown: 'email', locationQuery: {} }, () => {
        this.typeAheadInput.toggleDropdown();
        return analyticsIntegrations.searchEmail(results, queryObj, 'search');
      });
    }

    toggleModal (e, listToggle) {
      if (e) e.preventDefault();
      const { showModal } = this.state;
      if (_.isFunction(listToggle)) listToggle();
      this.setState({ showModal: !showModal });
    }

    render () {
      const {
        location = '',
        predictions,
        metros,
        locationPlaceholder,
        whichDropdown,
        locationQuery
      } = this.state;
      return (
        <div>
          <Component
            inputOverlayRef={e => { this.typeAheadInput = e; }}
            inputRef={this.props.inputRef}
            location={location}
            locations={predictions}
            dataLocation={!_.isEmpty(locationQuery) ? JSON.stringify(locationQuery) : null}
            onBlurLocation={this.onBlurLocation}
            onFocusLocation={this.onFocusLocation}
            onChange={this.onChange}
            onKeyDown={this.onKeyDown}
            clickMetro={this.metroClick}
            clickPoi={this.poiClick}
            toggleModal={this.toggleModal}
            locationPlaceholder={locationPlaceholder}
            metros={metros}
            whichDropdown={whichDropdown}
            inputClassName={`${this.props.inputClassName} ${this.state.placeholderColor}`}
            inputAttributes={{
              onClick: this.onInputOnclick
            }}
            addOnImage={this.props.addOnImage}
          />
          <LocationModal
            metros={this.state.metros}
            toggleModal={this.toggleModal}
            showModal={this.state.showModal}
            clickMetro={this.metroClick}
          />
        </div>
      );
    }
  }
  HOC.propTypes = {
    searchOnMount: PropTypes.bool,
    filterResults: PropTypes.func,
    currentBounds: PropTypes.string,
    inputClassName: PropTypes.string,
    inputRef: PropTypes.func,
    locationRef: PropTypes.func,
    addOnImage: PropTypes.object
  };
  return HOC;
};

export default withLocationDropdownHoc;
